/** @format */

import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
} from "@mui/material";
import {
  AutoAwesome,
  Lightbulb,
} from "@mui/icons-material";

// Styling Tokens consistent with GetInsights theme
const STYLES = {
  colors: {
    primary: "#000088",
    secondary: "#262655",
    accent: "#4A3D9E",
    textPrimary: "#1A1A1A",
    textSecondary: "#555577",
    border: "#D5D9EB",
    borderLight: "#E3E6F0",
    background: "#F3FBFF",
    backgroundWhite: "#FFFFFF",
    warning: "#DD6B20",
    danger: "#E53E3E",
    success: "#28A745",
    info: "#3182CE",
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
  borderRadius: {
    card: "24px",
    small: "14px",
  },
  boxShadow: {
    card: "0 4px 20px 0 rgba(0,0,0,0.06)",
    cardHover: "0 8px 30px 0 rgba(0,0,0,0.12)",
  },
};

// Urgency badge configuration
const URGENCY_CONFIG = {
  IMMEDIATE: { color: "#E53E3E", bg: "#FFF5F5", label: "Immediate Urgency" },
  WITHIN_6_MONTHS: { color: "#DD6B20", bg: "#FFFAF0", label: "Within 6 Months" },
  WITHIN_1_YEAR: { color: "#3182CE", bg: "#EBF8FF", label: "Within 1 Year" },
};

function SurvivalStrategySection({
  disruptionData,
  disruptionLoading,
  disruptionError,
  checkSkill,
}) {
  // Only render when we have valid data
  if (disruptionLoading || !disruptionData || disruptionError) return null;

  const survival = disruptionData?.survivalStrategy || {};

  // Only render if we have skills to learn
  if (!survival?.topSkillsToLearn) return null;

  return (
    <Grid item xs={12}>
      <Box
        id="survival-strategy-section"
        sx={{
          border: "1.5px solid #000088",
          borderRadius: { xs: "24px", sm: "36px" },
          background: STYLES.colors.background,
          boxSizing: "border-box",
          overflow: "hidden",
          width: "100%",
        }}
        p={{ xs: 1.5, sm: 3, md: 4 }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Lightbulb sx={{ fontSize: 36, color: "#FFD700" }} />
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              textAlign: "center",
              fontWeight: 700,
              color: STYLES.colors.primary,
            }}
          >
            Actionable Survival Strategy & Up-Skilling for {checkSkill}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #1A1A40 0%, #262655 100%)",
            borderRadius: STYLES.borderRadius.card,
            p: { xs: 2.5, sm: 4 },
            color: "#FFFFFF",
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              fontSize: "13px",
              color: "#D5D9EB",
              mb: 3,
            }}
          >
            Stay ahead of AI automation by acquiring high-leverage skills and positioning yourself strategically.
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {survival.topSkillsToLearn.map((skillItem, idx) => {
              const urg = URGENCY_CONFIG[skillItem.urgency] || URGENCY_CONFIG.WITHIN_6_MONTHS;
              return (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "16px",
                      background: "rgba(255, 255, 255, 0.07)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      height: "100%",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1, gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: STYLES.typography.fontFamily,
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#FFFFFF",
                          }}
                        >
                          {skillItem.skill}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: "12px",
                          color: "#CBD5E0",
                          lineHeight: 1.5,
                          mb: 2,
                        }}
                      >
                        {skillItem.reason}
                      </Typography>
                    </Box>
                    <Chip
                      label={urg.label}
                      size="small"
                      sx={{
                        fontFamily: STYLES.typography.fontFamily,
                        fontSize: "10px",
                        fontWeight: 800,
                        alignSelf: "flex-start",
                        background: urg.bg,
                        color: urg.color,
                      }}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          {/* Career Pivot Suggestions */}
          {survival.careerPivotSuggestions && survival.careerPivotSuggestions.length > 0 && (
            <Box
              sx={{
                pt: 2.5,
                borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#A0AEC0",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Recommended Career Pivots:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {survival.careerPivotSuggestions.map((pivot, i) => (
                  <Chip
                    key={i}
                    label={pivot}
                    icon={<AutoAwesome sx={{ fontSize: 12, color: "#FFD700 !important" }} />}
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontSize: "12px",
                      fontWeight: 600,
                      background: "rgba(255, 255, 255, 0.15)",
                      color: "#FFFFFF",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      "& .MuiChip-label": { px: 1.5 },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Grid>
  );
}

export default SurvivalStrategySection;
